/**
 * Rebind operations + action labels (#70) — the PURE, testable core of the
 * keybinding settings UI.
 *
 * All functions here are pure keymap→keymap transforms; the React settings
 * screen is a thin shell over them. Conflicts are surfaced by the existing
 * `findConflicts` (keymap.ts) — not re-implemented here.
 *
 * v1 is SINGLE-BIND per action in the editor: `rebindAction` replaces an
 * action's chord list with exactly one chord. The underlying `Keymap` model
 * already supports multiple chords per action, so multi-bind editing is a
 * later additive change — these helpers just constrain the editor to one for
 * now.
 */

import type { ChampionId } from "@pit/protocol";
import type { GameAction } from "./actions.js";
import { gameActionId } from "./actions.js";
import type { KeyChord } from "./chord.js";
import type { Keymap, KeyBinding } from "./keymap.js";
import { abilityLabel } from "../../champions/abilityLabels.js";
import { abilityDescription } from "../../champions/abilityFacts.js";

/** Human-readable label for a `GameAction`. Config-authorability note: this
 *  labels SLOTS + static actions, never ability identities — a config-authored
 *  champion's slot shows as "Ability N" with no per-champion label code. */
export function actionLabel(action: GameAction): string {
  switch (action.kind) {
    case "castSlot":
      return `Ability ${String((action.slot as number) + 1)}`;
    case "targetNearestEnemy":
      return "Target nearest enemy";
    case "cycleEnemyTarget":
      return "Target next enemy";
    case "cycleAllyTarget":
      return "Cycle friendly target";
    case "clearHostile":
      return "Clear target";
    case "clearFriendly":
      return "Clear friendly target";
    case "arenaTarget":
      return `Target arena ${String(action.index + 1)}`;
    case "partyTarget":
      return `Target party ${String(action.index + 1)}`;
    case "stopCasting":
      return "Stop Casting";
    case "toggleAutoAttack":
      return "Toggle Auto-Attack";
    case "openChat":
      return "Chat";
    default:
      return assertNever(action);
  }
}

/**
 * Hover-tooltip text for a `settings-row-*` (#462). Exhaustive switch mirrors
 * `actionLabel`'s shape exactly — a new `GameAction` variant is a compile
 * error here until described, via `assertNever`.
 *
 * Static actions get a fixed, accurate one-line description of what pressing
 * the bound key does. `arenaTarget`/`partyTarget` fold their 1-based slot
 * number in, same as `actionLabel`.
 *
 * `castSlot` resolves the REAL ability currently in that slot for
 * `championId` — name + authored description, sourced from `abilityLabels.ts`
 * / `abilityFacts.ts` (the single auto-derived description source, #461) —
 * NOT a second hand-copied table. An empty slot (no ability assigned to this
 * champion's loadout there) falls back to a generic, honest description
 * rather than showing nothing.
 */
export function actionTooltip(action: GameAction, championId: ChampionId): string {
  switch (action.kind) {
    case "castSlot": {
      const slot = action.slot as number;
      const name = abilityLabel(championId, slot);
      if (name === null) return `Casts the ability in loadout slot ${String(slot + 1)}.`;
      const description = abilityDescription(championId, slot);
      return description === null ? name : `${name}: ${description}`;
    }
    case "targetNearestEnemy":
      return "Selects the nearest enemy as your hostile target.";
    case "cycleEnemyTarget":
      return "Cycles your hostile target to the next enemy in seat order.";
    case "cycleAllyTarget":
      return "Cycles your friendly target to the next ally (self included).";
    case "clearHostile":
      return "Clears your current hostile target.";
    case "clearFriendly":
      return "Clears your current friendly target.";
    case "arenaTarget":
      return `Directly targets arena slot ${String(action.index + 1)} as your hostile target.`;
    case "partyTarget":
      return `Directly targets party slot ${String(action.index + 1)} as your friendly target.`;
    case "stopCasting":
      return "Immediately cancels your current cast or channel.";
    case "toggleAutoAttack":
      return "Toggles your auto-attack on or off.";
    case "openChat":
      return "Opens the chat box. Press again to send, Escape to cancel. While it is open, your gameplay keys are captured by the chat box and fire nothing.";
    default:
      return assertNever(action);
  }
}

/**
 * Replace the chord bound to `action` with exactly `chord` (single-bind v1).
 * Other actions are untouched. If the action isn't in the keymap it's left
 * as-is (the editor only offers actions that exist). Pure.
 */
export function rebindAction(keymap: Keymap, action: GameAction, chord: KeyChord): Keymap {
  const wantId = gameActionId(action);
  const bindings: KeyBinding[] = keymap.bindings.map((b) =>
    gameActionId(b.action) === wantId ? { action: b.action, chords: [chord] } : b,
  );
  return { bindings };
}

/**
 * Clear all chords for `action` (unbind). The action stays in the keymap with
 * an empty chord list, so it's still listed in the editor (rebindable) but
 * fires nothing. Pure.
 */
export function clearAction(keymap: Keymap, action: GameAction): Keymap {
  const wantId = gameActionId(action);
  const bindings: KeyBinding[] = keymap.bindings.map((b) =>
    gameActionId(b.action) === wantId ? { action: b.action, chords: [] } : b,
  );
  return { bindings };
}

/**
 * Restore a single action's binding to its default. Looks the action up in
 * `defaults`; if the default has no binding for it, clears it. Pure.
 */
export function resetAction(keymap: Keymap, action: GameAction, defaults: Keymap): Keymap {
  const wantId = gameActionId(action);
  const defBinding = defaults.bindings.find((b) => gameActionId(b.action) === wantId);
  const bindings: KeyBinding[] = keymap.bindings.map((b) => {
    if (gameActionId(b.action) !== wantId) return b;
    return defBinding ?? { action: b.action, chords: [] };
  });
  return { bindings };
}

/** Restore the entire keymap to defaults. Pure (returns the defaults as-is). */
export function resetAll(defaults: Keymap): Keymap {
  return { bindings: defaults.bindings.map((b) => ({ action: b.action, chords: [...b.chords] })) };
}

function assertNever(x: never): never {
  throw new Error(`unhandled GameAction in actionLabel: ${JSON.stringify(x)}`);
}
